import { BRAND_SITE_HOST } from "@/lib/brandConfig";
/**
 * Política de geo del sitio — una sola fuente para template + i18n.
 *
 * Reglas (alineadas con Index.tsx: BaseTemplate claro vs International vs home internacional admin):
 * - País edge normalizado AR → español por defecto y bucket “Argentina / home”.
 * - UNKNOWN / sin geo en anaafernandez.com → español por defecto (VPN, Worker sin CF-Country, etc.);
 *   si el Worker fija user_country no-AR o country no-AR en config, no se fuerza.
 * - Cookie user_country=AR → refuerzo cuando __TEMPLATE_CONFIG__ aún no está.
 * - test_country en localStorage (solo debug).
 *
 * No aplica a visitantes con país explícito no-AR (p. ej. US): ahí esta función es false.
 */

import { readEdgeCountryCookie } from "@/lib/i18nEdgeSync";
import { getServerTemplateConfig } from "@/lib/serverTemplateDetection";

/**
 * Código de país ISO2 o el literal UNKNOWN (fallback de useTemplateSelection sin Worker).
 */
export function normalizeCountryCode(
  code: string | null | undefined,
): string | null {
  if (code == null || typeof code !== "string") return null;
  const t = code.trim().toUpperCase();
  if (t === "UNKNOWN") return "UNKNOWN";
  if (t.length < 2) return null;
  return t.slice(0, 2);
}

/** Admin debug storage only when URL explicitly opts in (same rule as home template). */
function hasExplicitGeoPreviewUrl(): boolean {
  try {
    const params = new URLSearchParams(window.location.search);
    const preview = params.get("preview");
    if (preview === "international" || preview === "argentina") return true;
    const c = params.get("country");
    if (c && /^[a-zA-Z]{2}$/.test(c.trim())) return true;
    if (params.get("geo_sim") === "1") return true;
  } catch {
    /* ignore */
  }
  return false;
}

/**
 * True cuando el sitio debe usar español por defecto y el mismo criterio que
 * “visitante tipo Argentina” en la home (template negro, sin wishlist internacional).
 *
 * Sticky admin_preview / test_country are ignored unless URL is explicit so
 * real visitors always follow Worker geo (AR Exclusive / intl DualCTA).
 */
export function usesSpanishDefaultForSite(): boolean {
  if (typeof window === "undefined") return false;

  if (hasExplicitGeoPreviewUrl()) {
    try {
      const test = localStorage.getItem("test_country");
      const testNorm = normalizeCountryCode(test);
      if (testNorm === "AR") return true;
      if (testNorm && testNorm !== "UNKNOWN") return false;
    } catch {
      /* ignore */
    }

    try {
      const preview = sessionStorage.getItem("admin_preview_country");
      const previewNorm = normalizeCountryCode(preview);
      if (previewNorm === "AR") return true;
      if (previewNorm && previewNorm !== "UNKNOWN") return false;
    } catch {
      /* ignore */
    }
  }

  const edgeCountry = readEdgeCountryCookie();
  if (edgeCountry === "AR") return true;

  const cfg = getServerTemplateConfig();
  const c = normalizeCountryCode(cfg?.country);
  if (c === "AR") return true;

  // Worker / config dicen país concreto no-AR → plantilla internacional, no bucket AR
  if (edgeCountry && edgeCountry !== "AR") return false;
  if (c && c !== "UNKNOWN" && c !== "AR") return false;

  // Sin país fiable (UNKNOWN / null): en producción el sitio es Argentina-first
  try {
    const host = window.location.hostname.replace(/^www\./, "");
    if (host === BRAND_SITE_HOST) return true;
  } catch {
    /* ignore */
  }

  return false;
}
